import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GainsUiCommonsModule } from '@gainsystems/gains-ui-commons';
import { HighchartsChartComponent } from 'highcharts-angular';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { EllipsisMenuItem } from '@gainsystems/gains-ui-commons';
import Highcharts from 'highcharts';

export interface ForecastDataPoint {
  monthKey: string;
  actualValue?: number | null;
  forecastValue?: number | null;
}

@Component({
  selector: 'forecast-widget',
  standalone: false,
  templateUrl: './forecast-widget.component.html',
  styleUrl: './forecast-widget.component.scss'
})
export class ForecastWidgetComponent implements OnInit, OnChanges {
  @Input() title = 'Forecast Accuracy';
  @Input() tooltipText = 'Measures how closely actual values match forecasted values over time.';
  @Input() data: ForecastDataPoint[] = []; // Unified data structure
  @Input() actualData: ForecastDataPoint[] = []; // Legacy support
  @Input() forecastData: ForecastDataPoint[] = []; // Legacy support
  @Input() widthClass = 'chart-small';
  @Input() menuItems: EllipsisMenuItem[] = [];
  @Input() widgetId = '';
  @Input() actualSeriesName = 'Actual';
  @Input() forecastSeriesName = 'Forecast';
  @Input() actualColor = '#0b6d7d';
  @Input() forecastColor = '#7BA428';
  @Input() currentPlanPeriod?: Date;
  @Input() showAccuracy = true;
  @Input() yAxisTitle = 'Value';

  @Output() changeEvent = new EventEmitter<string>();
  @Output() chartReady = new EventEmitter<Highcharts.Chart>();

  chart: Highcharts.Chart | undefined;
  chartOptions: Highcharts.Options = {};

  chartCallback: Highcharts.ChartCallbackFunction = chart => {
    this.chart = chart;
    this.chartReady.emit(chart);
  };

  ngOnInit() {
    this.updateChartOptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] || changes['actualData'] || changes['forecastData'] || changes['currentPlanPeriod']) {
      this.updateChartOptions();
    }
  }

  handleMenuAction(action: string): void {
    this.changeEvent.emit(action);
  }

  reflow(): void {
    this.chart?.reflow();
  }

  private updateChartOptions(): void {
    // Use unified data structure if provided, otherwise fall back to separate arrays
    const dataSource = this.data.length > 0 ? this.data : this.mergeDataSources();

    const months = this.computeRolling12Months(this.currentPlanPeriod);
    const categories = months.map(month => {
      const date = new Date(month + '-01');
      return new Intl.DateTimeFormat('en', { month: 'short', year: '2-digit' }).format(date);
    });

    const actualSeries: (number | null)[] = [];
    const forecastSeries: (number | null)[] = [];
    const allValues: number[] = [];

    months.forEach(month => {
      const dataPoint = dataSource.find((d: ForecastDataPoint) => d.monthKey === month);
      const actualValue = dataPoint?.actualValue ?? null;
      const forecastValue = dataPoint?.forecastValue ?? null;

      actualSeries.push(actualValue);
      forecastSeries.push(forecastValue);

      if (actualValue !== null) allValues.push(actualValue);
      if (forecastValue !== null) allValues.push(forecastValue);
    });

    const yAxisMax = this.determineYAxisMax(allValues);
    const tickInterval = this.chooseNiceTickInterval(yAxisMax);

    this.chartOptions = {
      chart: {
        type: 'area',
        plotBorderWidth: 1,
        backgroundColor: 'transparent',
        events: {
          load: () => {
            setTimeout(() => {
              if (this.chart?.container) {
                this.chart.container.onmouseleave = () => {
                  if ((this.chart as any)?.verticalLine) {
                    (this.chart as any).verticalLine.destroy();
                    (this.chart as any).verticalLine = null;
                  }
                };
              }
            }, 0);
          }
        }
      },
      title: { text: '' },
      xAxis: {
        categories: categories,
        title: { text: '' },
        gridLineWidth: 0
      },
      yAxis: {
        min: 0,
        max: yAxisMax,
        tickInterval: tickInterval,
        title: { text: this.yAxisTitle, align: 'middle' },
        gridLineWidth: 1,
        gridLineColor: '#e6e6e6'
      },
      tooltip: {
        formatter: function () {
          const point = this as any;
          const pointIndex = point.point.index;
          const actual = actualSeries[pointIndex];
          const forecast = forecastSeries[pointIndex];

          let accuracyText = '—';
          if (actual !== null && actual !== 0 && forecast !== null) {
            const mape = Math.abs(actual - forecast) / actual;
            const accuracy = (1 - mape) * 100;
            accuracyText = accuracy.toFixed(1) + '%';
          }

          const actualText = actual !== null ? actual.toFixed(0) : '—';
          const forecastText = forecast !== null ? forecast.toFixed(0) : '—';

          return `<b>${categories[pointIndex]}</b><br/>` + `Actual: ${actualText}<br/>` + `Forecast: ${forecastText}<br/>` + `Accuracy: ${accuracyText}`;
        }
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        y: 0
      },
      credits: { enabled: false },
      plotOptions: {
        area: {
          fillOpacity: 0.12,
          lineWidth: 2,
          marker: {
            enabled: true,
            radius: 4,
            states: {
              hover: {
                enabled: true,
                radius: 6
              }
            }
          },
          states: { hover: { enabled: true } }
        },
        series: {
          connectNulls: true,
          gapSize: 1,
          marker: { enabled: true },
          point: {
            events: {
              mouseOver: function () {
                const chart = this.series.chart;
                const xPos = (this.plotX || 0) + chart.plotLeft;
                if ((chart as any).verticalLine) (chart as any).verticalLine.destroy();
                (chart as any).verticalLine = chart.renderer
                  .path([
                    ['M', xPos, chart.plotTop],
                    ['L', xPos, chart.plotTop + chart.plotHeight]
                  ])
                  .attr({ 'stroke-width': 1, stroke: '#666666', dashstyle: 'dash' })
                  .add();
              }
            }
          }
        }
      },
      colors: [this.actualColor, this.forecastColor],
      series: [
        {
          type: 'area',
          name: this.actualSeriesName,
          data: actualSeries,
          color: this.actualColor
        },
        {
          type: 'area',
          name: this.forecastSeriesName,
          data: forecastSeries,
          color: this.forecastColor
        }
      ],
      exporting: {
        enabled: false,
        fallbackToExportServer: false
      }
    };
  }

  private computeRolling12Months(currentPlanPeriod?: Date): string[] {
    const baseDate = currentPlanPeriod || new Date();
    const result: string[] = [];

    for (let i = 11; i >= 0; i--) {
      const date = new Date(baseDate);
      date.setMonth(date.getMonth() - i);
      result.push(this.normalizeMonthKey(date));
    }

    return result;
  }

  private normalizeMonthKey(dateOrKey: string | Date): string {
    if (typeof dateOrKey === 'string') {
      if (/^\d{4}-\d{2}$/.exec(dateOrKey)) {
        return dateOrKey;
      }
      if (/^\d{4}-\d{2}-\d{2}/.exec(dateOrKey)) {
        return dateOrKey.substring(0, 7);
      }
    }

    const date = typeof dateOrKey === 'string' ? new Date(dateOrKey) : dateOrKey;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  private getValueForMonth(data: ForecastDataPoint[], monthKey: string, valueKey: 'actualValue' | 'forecastValue'): number | null {
    const item = data.find(d => d.monthKey === monthKey);
    const value = item?.[valueKey];
    return value !== undefined && value !== null && !isNaN(value) ? value : null;
  }

  private calculateAccuracy(actual: number | null, forecast: number | null): number | null {
    if (actual === null || actual === 0 || forecast === null) {
      return null;
    }

    const mape = Math.abs(actual - forecast) / actual;
    const accuracy = (1 - mape) * 100;
    return accuracy;
  }

  private determineYAxisMax(values: number[]): number {
    if (values.length === 0) return 100;
    const max = Math.max(...values);
    return max * 1.12;
  }

  private chooseNiceTickInterval(maxValue: number): number {
    const intervals = [1, 5, 10, 50, 100, 500, 1000, 5000, 10000];
    const targetTicks = 5;
    const roughInterval = maxValue / targetTicks;

    for (const interval of intervals) {
      if (interval >= roughInterval) {
        return interval;
      }
    }

    const magnitude = Math.pow(10, Math.floor(Math.log10(roughInterval)));
    return Math.ceil(roughInterval / magnitude) * magnitude;
  }

  // Export methods that can be called from parent
  exportCSV(): void {
    (this.chart as any)?.downloadCSV();
  }

  exportExcel(): void {
    (this.chart as any)?.downloadXLS();
  }

  exportPDF(): void {
    (this.chart as any)?.exportChart({
      type: 'application/pdf'
    });
  }

  exportHTML(): void {
    const htmlTable = (this.chart as any)?.getTable();
    if (!htmlTable) {
      console.warn('Chart table export is not available.');
      return;
    }

    const fullHTML = this.generateStyledHTML(htmlTable);
    const blob = new Blob([fullHTML], { type: 'text/html' });
    this.downloadFile(blob, `${this.title.toLowerCase().replace(/\s+/g, '-')}-data.html`);
  }

  private generateStyledHTML(table: string): string {
    return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <title>${this.title} - Exported Data</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
          }
          table {
            border-collapse: collapse;
            width: auto;
          }
          th, td {
            border: 1px solid black;
            padding: 8px 12px;
            text-align: left;
          }
          th {
            background-color: #f2f2f2;
          }
        </style>
      </head>
      <body>
        <h1>${this.title}</h1>
        ${table}
      </body>
    </html>
  `.trim();
  }

  private downloadFile(blob: Blob, name: string): void {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
  }

  private mergeDataSources(): ForecastDataPoint[] {
    // Merge separate actualData and forecastData arrays into unified structure
    const allMonths = new Set<string>();
    this.actualData.forEach(item => allMonths.add(item.monthKey));
    this.forecastData.forEach(item => allMonths.add(item.monthKey));

    return Array.from(allMonths).map(monthKey => {
      const actualItem = this.actualData.find(item => item.monthKey === monthKey);
      const forecastItem = this.forecastData.find(item => item.monthKey === monthKey);

      return {
        monthKey,
        actualValue: actualItem?.actualValue ?? null,
        forecastValue: forecastItem?.forecastValue ?? null
      };
    });
  }
}
